import { create } from 'zustand';
import useLoadingStore from './use-loading';
import useProfileStore from './use-profile';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';
import { removeProfile } from 'utils/profile-storage';
import { removeActiveCaregiver } from 'utils/caregiver-storage';
import {
  getAuthAndUser,
  setAuthAndUser,
  setTwoFactorAuth,
  getTwoFactorAuth,
  removeAuthAndUser,
  removeTwoFactorAuth,
} from 'utils/auth-user-storage';

const { user, authToken } = getAuthAndUser();
const { twoFactorAuth } = getTwoFactorAuth();

const useAuthStore = create((set) => ({
  user: user || null,
  token: authToken || null,
  twoFactorAuth: twoFactorAuth || { enabled: null, method: null },

  // Function to login
  login: async ({ data, successCallback, errorCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.LOGIN,
        data
      );
      if (response.success) {
        if (!response.data?.otpRequired) {
          set({
            user: response.data.user,
            token: response.data.token,
          });
          setAuthAndUser(response.data.token, response.data.user);
        }
        successCallback(response.data);
      }
    } catch (error) {
      errorCallback && errorCallback(error);
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to verify otp
  verifyOtp: async ({ data, successCallback, errCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.VERIFY_OTP,
        data
      );
      if (response.success) {
        set({
          user: response.data.user,
          token: response.data.token,
        });
        setAuthAndUser(response.data.token, response.data.user);
        successCallback && successCallback(response);
      }
    } catch (error) {
      errCallback && errCallback(error);
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to resend otp
  resendOtp: async ({ payload, successCallback }) => {
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.RESEND_OTP,
        payload
      );
      response.success && successCallback(response);
    } catch (error) {}
  },

  // Function to forget password
  forgetPassword: async ({ payload, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.FORGET_PASSWORD,
        payload
      );
      response.success && successCallback();
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to reset password
  resetPassword: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CHANGE_PASSWORD,
        data
      );
      response.success && successCallback();
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to signup applicant
  signUpOfApplicant: async ({ data, successCallback, errorCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        'POST',
        API_ENDPOINTS.SIGN_UP,
        data
      );
      if (response.success) {
        successCallback(response);
      }
    } catch (error) {
      errorCallback && errorCallback(error);
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to login applicant
  loginOfApplicant: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        'POST',
        API_ENDPOINTS.APPLICANT_LOGIN,
        data
      );

      if (response.success) {
        if (!response.data?.otpRequired) {
          set({
            user: response.data.user,
            token: response.data.token,
          });
          setAuthAndUser(response.data.token, response.data.user);
        }
        successCallback(response.data);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to verify otp of applicant
  verifyOtpOfApplicant: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        'POST',
        API_ENDPOINTS.APPLICANT_VERIFY_OTP,
        data
      );

      if (response.success) {
        set({
          user: response.data.user,
          token: response.data.token,
        });
        setAuthAndUser(response.data.token, response.data.user);
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to resend otp of applicant
  resendOtpOfApplicant: async ({ payload, successCallback }) => {
    try {
      const { data: response } = await makeRequest(
        'POST',
        API_ENDPOINTS.APPLICANT_RESEND_OTP,
        payload
      );
      response.success && successCallback(response);
    } catch (error) {}
  },

  // Function to forget password of applicant
  forgetPasswordOfApplicant: async ({ payload, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        'POST',
        API_ENDPOINTS.APPLICANT_FORGET_PASSWORD,
        payload
      );
      response.success && successCallback();
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to reset password of applicant
  resetPasswordOfApplicant: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        'POST',
        API_ENDPOINTS.APPLICANT_CHANGE_PASSWORD,
        data
      );
      response.success && successCallback();
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to toggle 2fa
  toggle2fa: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.TOGGLE_2FA,
        data
      );
      if (response.success) {
        const updatedTwoFactorAuth = {
          method: data.method,
          enabled: data.enable,
        };
        setTwoFactorAuth(updatedTwoFactorAuth);
        set({ twoFactorAuth: updatedTwoFactorAuth });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to set twoFactorAuth
  setTwoFactorAuth: ({ data }) => {
    setTwoFactorAuth(data);
    set({ twoFactorAuth: data });
  },

  // Function to logout
  logout: ({ successCallback }) => {
    set({
      user: null,
      authToken: null,
      twoFactorAuth: { enabled: null, method: null },
    });
    useProfileStore.getState().resetProfile();
    removeProfile();
    removeAuthAndUser();
    removeTwoFactorAuth();
    removeActiveCaregiver();
    successCallback();
  },
}));

export default useAuthStore;
